import { useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import { MDSModal } from "@/mds";
import type {
  AppId,
  WelcomeWizardGroup,
  WelcomeWizardProps,
  WelcomeStepContext,
} from "./welcome-wizard.types";
import { WizardContainer } from "./welcome-wizard.styles";
import { Steps } from "./welcome-wizard.config";
import styled from "@emotion/styled";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

export const WelcomeWizard = observer<WelcomeWizardProps>(
  ({ isOpen, onClose, initialStep = "app-selection" }) => {
    // Generic context state instead of import-specific state
    const [stepContext, setStepContext] = useState<WelcomeStepContext>({
      // Import-specific data
      wizardType: "welcome",
      selectedAppId: null as AppId | null,
      customAppName: "",
      // Can be extended for other step types
    });

    const [wizardStep, _setWizardStep] = useState<string>(initialStep);
    const stepConfig = Steps[wizardStep];

    const gotoWizardStep = useCallback(
      (step: string, nextAppId?: AppId) => {
        _setWizardStep(step);

        trackEvent(TrackedEvent.ImportWizardStepChange, {
          from: wizardStep,
          to: step,
          appId: nextAppId ?? stepContext.selectedAppId,
        });
      },
      [stepContext.selectedAppId, wizardStep]
    );

    const handleClose = (group: WelcomeWizardGroup, ...rest: string[]) => {
      onClose(group, ...rest);
    };

    const handleModalClose = () => {
      // Prevent closing the modal via ESC or clicking outside
      // Only allow closing via the Skip button
      // There are explicit "close" buttons in the various app-specific flows
      return;
    };

    // Generic context update handler
    const handleUpdateContext = useCallback((updates: Partial<WelcomeStepContext>) => {
      setStepContext(prev => ({ ...prev, ...updates }));
    }, []);

    const handleTabSelect = useCallback(
      (appId: AppId) => {
        let nextWizardStep = "other-apps-1";

        switch (appId) {
          case "apple-notes":
            nextWizardStep = "apple-notes-1";
            handleUpdateContext({ selectedAppId: appId });
            break;
          case "obsidian":
            nextWizardStep = "obsidian-1";
            handleUpdateContext({ selectedAppId: appId });
            break;
          case "other":
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: appId });
            break;
          default:
            nextWizardStep = "other-apps-1";
            handleUpdateContext({ selectedAppId: "other" });
            break;
        }

        gotoWizardStep(nextWizardStep, appId);
      },
      [gotoWizardStep, handleUpdateContext]
    );

    const handleSkip = useCallback(() => {
      trackEvent(TrackedEvent.ImportWizardSkip, {
        appId: stepContext.selectedAppId,
        customAppName: stepContext.customAppName,
        step: wizardStep,
      });
      gotoWizardStep("skip-confirmation");
    }, [gotoWizardStep, stepContext, wizardStep]);

    return (
      <MDSModal isOpen={isOpen} handleClose={handleModalClose}>
        <ModalContent>
          <WizardContainer data-component="wizard-container">
            {stepConfig && (
              <StepCrossFade key={wizardStep} data-wizard-step={wizardStep}>
                {stepConfig.render({
                  // New generic props
                  context: stepContext,
                  onUpdateContext: handleUpdateContext,
                  onNavToStep: (step: string) => {
                    gotoWizardStep(step);
                  },
                  onClose: (...rest: string[]) => handleClose(stepConfig.group, ...rest),
                  onSkip: () => handleSkip(),

                  // Props for import steps
                  selectedAppId: stepContext.selectedAppId || null,
                  customAppName: stepContext.customAppName || "",
                  onSetSelectedApp: (appId: AppId) => handleUpdateContext({ selectedAppId: appId }),
                  onSetCustomAppName: (name: string) =>
                    handleUpdateContext({ customAppName: name }),
                  handleTabSelect,
                })}
              </StepCrossFade>
            )}
          </WizardContainer>
        </ModalContent>
      </MDSModal>
    );
  }
);

const StepCrossFade = styled.div<{ top?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  margin-top: ${({ top }) => top ?? "0px"};
  min-height: 0; /* allow inner content to shrink and scroll */
`;

const ModalContent = styled.div`
  overflow: hidden;
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
`;
